import React, { SetStateAction, useEffect, useState, Dispatch } from 'react';
import { Form, Row, Col, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface MultiselectBoxProps<T extends object> {
    id: string,
    name?: string,
    options: T[],
    selectedOptions: T[],
    setSelectedOptions: Dispatch<SetStateAction<T[]>>;
    map: keyof T,
    errors?: any,
    required?: boolean
}

function MultiselectBox<T extends object>({ id, name, options, selectedOptions, setSelectedOptions, map, errors, required }: MultiselectBoxProps<T>) {
    const { t } = useTranslation();
    const [internalSelectedOptions, setInternalSelectedOptions] = useState(selectedOptions);
    const [isInvalid, setIsInvalid] = useState(false);

    useEffect(() => {
        setSelectedOptions(internalSelectedOptions);
    }, [internalSelectedOptions, setSelectedOptions]);

    useEffect(() => {
        // Prevent unnecessary re-renders by checking if any meaningful change was made and making no changes if not
        // Without this check it becomes very easy to trigger infinite re-renders in consuming components
        if (internalSelectedOptions.every(option => selectedOptions.some(opt => opt[map] === option[map]))
            && selectedOptions.every(option => internalSelectedOptions.some(opt => opt[map] === option[map]))) {
            return;
        }
        setInternalSelectedOptions(selectedOptions);
        // disable exhaustive deps warning on internal selected options, it is only used to check if we should react to a changing selected options
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, selectedOptions]);

    const selectOption = (option: T) => {
        setInternalSelectedOptions((prev) =>
            prev.find(item => item[map] === option[map])
                ? prev.filter((item) => item[map] !== option[map])
                : [...prev, option]
        );

        setIsInvalid(false);
    };

    return (
        <Form.Group as={Row} controlId={id}>
            {name &&
                <Form.Label column xs={2} sm={3}>{name}:</Form.Label>
            }
            <Col xs={10} sm={9} className="d-flex justify-content align-items-center">
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="light" id="dropdown-basic"
                        className={`form-control d-flex justify-content-between align-items-center ${isInvalid ? 'is-invalid' : ''}`} >
                        <span className="ms-1 me-1">
                            {internalSelectedOptions.length > 0
                                ? internalSelectedOptions?.map(o => `${o[map]}`).join(", ")
                                : t<string>("Application.Controls.DefaultSelect")
                            }
                        </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {options?.map((option) => (
                            <Form.Check
                                type="checkbox"
                                className="ms-3"
                                label={`${option[map]}`}
                                checked={internalSelectedOptions.some(o => o[map] === option[map])}
                                onChange={() => selectOption(option)}
                            />
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </Col>
            {/* Hidden required control to manage validation */}
            <Form.Control
                required={required}
                hidden
                value={internalSelectedOptions.join(',')}
                onChange={() => { }}
                onInvalid={(e) => {
                    e.preventDefault(); // suppress browser tooltip
                    setIsInvalid(true);
                }}
            />
        </Form.Group>
    );
}

export default MultiselectBox;